import { parseStackFrames } from './console-buffer';
import type { ConsoleEntry } from './types';

export class ErrorListener {
  private consoleBuffer: { pushEntry(e: ConsoleEntry): void } | null = null;
  private errorHandler: ((e: ErrorEvent) => void) | null = null;
  private rejectionHandler: ((e: PromiseRejectionEvent) => void) | null = null;
  private installed = false;

  install(consoleBuffer: { pushEntry(e: ConsoleEntry): void }): void {
    if (this.installed) return;
    this.installed = true;
    this.consoleBuffer = consoleBuffer;

    this.errorHandler = (e: ErrorEvent) => {
      const entry: ConsoleEntry = {
        ts: new Date().toISOString(),
        level: 'error',
        message: e.message || 'Unknown error',
      };

      if (e.error instanceof Error) {
        entry.stack_frames = parseStackFrames(e.error.stack);
      } else if (e.filename) {
        entry.stack_frames = [{
          file: e.filename,
          line: e.lineno ?? 0,
          column: e.colno,
          fn: '<global>',
          in_app: !e.filename.includes('node_modules'),
        }];
      }

      this.consoleBuffer?.pushEntry(entry);
    };

    this.rejectionHandler = (e: PromiseRejectionEvent) => {
      const reason = e.reason;
      const message = reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : 'Unhandled promise rejection';

      const entry: ConsoleEntry = {
        ts: new Date().toISOString(),
        level: 'error',
        message: `[UnhandledRejection] ${message}`,
      };

      if (reason instanceof Error) {
        entry.stack_frames = parseStackFrames(reason.stack);
      }

      this.consoleBuffer?.pushEntry(entry);
    };

    window.addEventListener('error', this.errorHandler);
    window.addEventListener('unhandledrejection', this.rejectionHandler);
  }

  destroy(): void {
    if (!this.installed) return;
    if (this.errorHandler) {
      window.removeEventListener('error', this.errorHandler);
      this.errorHandler = null;
    }
    if (this.rejectionHandler) {
      window.removeEventListener('unhandledrejection', this.rejectionHandler);
      this.rejectionHandler = null;
    }
    this.consoleBuffer = null;
    this.installed = false;
  }
}
